import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useMemo, MouseEvent } from 'react';
import { pipe } from 'fp-ts/function';
import { array, nonEmptyArray, option } from 'fp-ts';
import {
    Account,
    AccountType,
    CustomerClassification,
    GuestInHouseBalanceAndPreauthorizations,
    GuestInHouseReportData,
    GuestInHouseVerificationPhoto,
    LoyaltyMembership,
} from '@mews-commander/data-access';
import { ColumnFilterType, Stack } from '@optimus-web/core';
import { lt } from '@mews/framework';
import { keyBy, keyById } from '@mews/fp';
import { orderByLabel, useLocalizeOption } from '@mews-commander/form';
import { WithServiceId } from '@mews-commander/types';
import { RouteLink } from '@mews-commander/ui';
import { RouteName } from '@mews-commander/enums';
import { useCommanderThunkDispatch } from '@mews-commander/utils';
import { AccountLinkNew } from '../../../../../../../components';
import { ResourceCategoryName, ResourceName } from '../../../../common';
import { ReservationsTableColumns } from '../../common';
import { AccountsRecord, GuestInHouseVerificationPhotoRecord, LoyaltyMembershipsRecord } from '../../types';
import {
    AccountClassifications,
    AccountLoyalty,
    AmountType,
    CompanionBalancePreauthorizationsList,
    CompanionCarNumberList,
    CompanionClassificationsList,
    CompanionCounts,
    CompanionLoyaltyList,
    CompanionNameList,
    NotesIconButton,
    ProductItemsButton,
    ReservationDateTimeUtc,
    CompanionVerificationPhotoList,
} from '../components';
import { openModal } from '../../../../../../app';
import { AdditionalColumns, ReservationsTableState } from './types';
import { useLoyaltyFilterOptions } from './useLoyaltyFilterOptions';

const columnHelper = createColumnHelper<GuestInHouseReportData>();

export const additionalColumnDependencies: Partial<Record<ReservationsTableColumns, string[]>> = {
    [ReservationsTableColumns.Space]: [AdditionalColumns.ResourceCategoryName],
    [ReservationsTableColumns.Companions]: [AdditionalColumns.CompanionNames],
} as const;

export interface UseColumnsParams extends WithServiceId {
    accounts?: Array<Account>;
    handleClassificationsClick: (event: MouseEvent<HTMLAnchorElement>, account: Account) => void;
    handleLoyaltyMembershipsClick: (
        event: MouseEvent<HTMLAnchorElement>,
        loyaltyMemberships: Array<LoyaltyMembership>
    ) => void;
    handleNotesClick: (data: GuestInHouseReportData, accounts: AccountsRecord) => void;
    handleProductsClick: (data: GuestInHouseReportData, accounts: AccountsRecord) => void;
    handleReservationInfoClick: (data: GuestInHouseReportData, accounts: AccountsRecord) => void;
    loyaltyMemberships?: Array<LoyaltyMembership>;
    verificationPhotos?: Array<GuestInHouseVerificationPhoto>;
    balanceAndPreauthorizations?: Array<GuestInHouseBalanceAndPreauthorizations>;
}

export const useColumns = ({
    accounts,
    verificationPhotos,
    handleClassificationsClick,
    handleLoyaltyMembershipsClick,
    handleNotesClick,
    handleProductsClick,
    handleReservationInfoClick,
    loyaltyMemberships,
    serviceId,
    balanceAndPreauthorizations,
}: UseColumnsParams) => {
    const dispatch = useCommanderThunkDispatch();
    const localizeOption = useLocalizeOption();
    const classificationOptions = useMemo(
        () =>
            pipe(
                Object.values(CustomerClassification),
                array.map((value) => {
                    const { label } = localizeOption('CustomerClassification', value);

                    return {
                        label,
                        value,
                    };
                }),
                array.sort(orderByLabel)
            ),
        [localizeOption]
    );
    const loyaltyOptions = useLoyaltyFilterOptions();

    return useMemo(() => {
        const accountsRecord: AccountsRecord = keyById(accounts);
        const verificationPhotosRecord: GuestInHouseVerificationPhotoRecord = keyBy('accountId', verificationPhotos);
        const balanceAndPreauthRecord = keyBy('companionId', balanceAndPreauthorizations);
        const loyaltyMembershipsRecord = pipe(
            loyaltyMemberships,
            option.fromNullable,
            option.flatMap(option.fromPredicate(array.isNonEmpty)),
            option.map(nonEmptyArray.groupBy<LoyaltyMembership>((loyaltyMembership) => loyaltyMembership.accountId)),
            option.getOrElse<LoyaltyMembershipsRecord>(() => ({}))
        );

        const handleModalClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            e.stopPropagation();

            const url = e.currentTarget.href;
            dispatch(openModal(url));
        };

        return [
            columnHelper.accessor('assignedResourceId', {
                id: ReservationsTableColumns.Space,
                enableSorting: true,
                header: () => lt('Space'),
                cell: (props) => (
                    <Stack vertical fill>
                        <RouteLink
                            route={RouteName.ResourceState}
                            params={{ id: props.getValue() }}
                            inherit
                            onClick={handleModalClick}
                        >
                            <ResourceName resourceId={props.getValue()} />
                        </RouteLink>
                    </Stack>
                ),
            }),
            columnHelper.accessor('assignedResourceId', {
                id: AdditionalColumns.ResourceCategoryName,
                header: '',
                cell: (props) => (
                    <Stack vertical fill>
                        <ResourceCategoryName resourceId={props.getValue()} short />
                    </Stack>
                ),
            }),

            columnHelper.accessor('ownerId', {
                id: ReservationsTableColumns.Customer,
                enableSorting: true,
                header: () => lt('Customer'),
                cell: (props) => {
                    const account = accountsRecord[props.getValue()];

                    return account ? (
                        <Stack vertical fill>
                            <AccountLinkNew account={account} inherit onClick={handleModalClick} />
                        </Stack>
                    ) : null;
                },
            }),
            columnHelper.accessor('ownerId', {
                id: ReservationsTableColumns.Loyalty,
                enableColumnFilter: true,
                meta: {
                    filtersType: ColumnFilterType.MultiSelect,
                    getMultiSelectOptions: () => loyaltyOptions,
                    showHeaderInColumnFilter: true,
                },
                header: () => lt('GuestInHouseReportColumnLoyaltyPrograms'),
                cell: (props) => {
                    const accountLoyaltyMemberships = loyaltyMembershipsRecord[props.getValue()];

                    if (!accountLoyaltyMemberships) return null;

                    return (
                        <Stack vertical fill>
                            <AccountLoyalty
                                loyaltyMemberships={accountLoyaltyMemberships}
                                onClick={(e) => handleLoyaltyMembershipsClick(e, accountLoyaltyMemberships)}
                            />
                        </Stack>
                    );
                },
            }),
            columnHelper.accessor('ownerId', {
                id: ReservationsTableColumns.Classifications,
                enableColumnFilter: true,
                meta: {
                    filtersType: ColumnFilterType.MultiSelect,
                    getMultiSelectOptions: () => classificationOptions,
                    showHeaderInColumnFilter: true,
                },
                header: () => lt('Classifications'),
                cell: (props) => {
                    const account = accountsRecord[props.getValue()];

                    if (account?.data.discriminator !== AccountType.Customer) return null;

                    return (
                        <Stack vertical fill>
                            <AccountClassifications
                                /**
                                 * We don't have icons mapping tooling yet for a new customerClassifications.
                                 * TODO: create new tooling and do refactoring after new guestsInHouse report
                                 * will be delivered.
                                 */
                                classifications={account.data.value.classifications}
                                onClick={(e) => handleClassificationsClick(e, account)}
                            />
                        </Stack>
                    );
                },
            }),
            columnHelper.accessor('startUtc', {
                id: ReservationsTableColumns.Arrival,
                header: () => lt('Arrival'),
                cell: (props) => (
                    <Stack vertical fill>
                        <ReservationDateTimeUtc
                            utcString={props.getValue()}
                            onClick={() => handleReservationInfoClick(props.row.original, accountsRecord)}
                        />
                    </Stack>
                ),
            }),
            columnHelper.accessor('endUtc', {
                id: ReservationsTableColumns.Departure,
                header: () => lt('Departure'),
                cell: (props) => (
                    <Stack vertical fill>
                        <ReservationDateTimeUtc
                            utcString={props.getValue()}
                            onClick={() => handleReservationInfoClick(props.row.original, accountsRecord)}
                        />
                    </Stack>
                ),
            }),
            columnHelper.accessor('productsCount', {
                id: ReservationsTableColumns.Products,
                header: () => lt('Products'),
                cell: (props) => (
                    <Stack vertical fill>
                        <ProductItemsButton
                            onClick={() => {
                                handleProductsClick(props.row.original, accountsRecord);
                            }}
                            productsCount={props.getValue()}
                        />
                    </Stack>
                ),
            }),
            columnHelper.accessor('notesCount', {
                id: ReservationsTableColumns.Notes,
                header: () => lt('Notes'),
                cell: (props) => (
                    <Stack vertical fill>
                        <Stack>
                            <NotesIconButton
                                onClick={() => {
                                    handleNotesClick(props.row.original, accountsRecord);
                                }}
                                notesCount={props.getValue()}
                            />
                        </Stack>
                    </Stack>
                ),
            }),
            columnHelper.accessor('occupancyData', {
                id: ReservationsTableColumns.Companions,
                header: () => lt('CompanionPlural'),
                cell: (props) => (
                    <Stack vertical fill>
                        <CompanionCounts serviceId={serviceId} occupancyData={props.getValue()} />
                    </Stack>
                ),
            }),
            columnHelper.accessor('companionIds', {
                id: AdditionalColumns.CompanionNames,
                header: () => '',
                cell: (props) => (
                    <Stack vertical fill>
                        <CompanionNameList
                            accounts={accountsRecord}
                            companionIds={props.getValue()}
                            onClick={handleModalClick}
                        />
                    </Stack>
                ),
            }),
            columnHelper.accessor('companionIds', {
                id: ReservationsTableColumns.CompanionLoyalty,
                header: () => lt('GuestInHouseReportColumnCompanionLoyaltyPrograms'),
                cell: (props) => (
                    <Stack vertical fill>
                        <CompanionLoyaltyList
                            companionIds={props.getValue()}
                            handleLoyaltyMembershipsClick={handleLoyaltyMembershipsClick}
                            loyaltyMembershipsRecord={loyaltyMembershipsRecord}
                        />
                    </Stack>
                ),
            }),
            columnHelper.accessor('companionIds', {
                id: ReservationsTableColumns.CompanionClassifications,
                header: () => lt('Classifications'),
                cell: (props) => (
                    <Stack vertical fill>
                        <CompanionClassificationsList
                            accounts={accountsRecord}
                            companionIds={props.getValue()}
                            handleClassificationsClick={handleClassificationsClick}
                        />
                    </Stack>
                ),
            }),
            columnHelper.accessor('companionIds', {
                id: ReservationsTableColumns.CarRegistrationNumber,
                header: () => lt('CarRegistrationNumber'),
                cell: (props) => (
                    <Stack vertical fill>
                        <CompanionCarNumberList accounts={accountsRecord} companionIds={props.getValue()} />
                    </Stack>
                ),
            }),
            columnHelper.accessor('companionIds', {
                id: ReservationsTableColumns.Preauthorizations,
                header: () => lt('GuestInHouseReportColumnPreauthorizations'),
                cell: (props) => (
                    <Stack vertical fill>
                        <CompanionBalancePreauthorizationsList
                            balanceAndPreauth={balanceAndPreauthRecord}
                            companionIds={props.getValue()}
                            amountType={AmountType.Preauthorizations}
                        />
                    </Stack>
                ),
            }),
            columnHelper.accessor('companionIds', {
                id: ReservationsTableColumns.Balance,
                header: () => lt('GuestInHouseReportColumnBalance'),
                cell: (props) => (
                    <Stack vertical fill>
                        <CompanionBalancePreauthorizationsList
                            balanceAndPreauth={balanceAndPreauthRecord}
                            companionIds={props.getValue()}
                            amountType={AmountType.Balance}
                        />
                    </Stack>
                ),
            }),
            columnHelper.accessor('companionIds', {
                id: ReservationsTableColumns.VerificationPhoto,
                header: () => lt('GuestInHouseReportColumnVerificationPhoto'),
                cell: (props) => (
                    <Stack vertical fill>
                        <CompanionVerificationPhotoList
                            verificationPhotos={verificationPhotosRecord}
                            companionIds={props.getValue()}
                        />
                    </Stack>
                ),
            }),
        ] as Array<ColumnDef<ReservationsTableState>>;
    }, [
        accounts,
        verificationPhotos,
        balanceAndPreauthorizations,
        classificationOptions,
        handleClassificationsClick,
        handleLoyaltyMembershipsClick,
        handleNotesClick,
        handleProductsClick,
        handleReservationInfoClick,
        loyaltyMemberships,
        loyaltyOptions,
        serviceId,
        dispatch,
    ]);
};
